// ── Shared settings store — admin changes propagate to rep & customer portals ──
// Handles commission rates (per rep), discount rates (per customer), and shipping rules
// Persists to localStorage so changes survive page refresh

const LS_KEY = "mpcare_shared_settings";

const DEFAULT_SHIPPING = {
  flatRate: 12.99,
  freeThreshold: 500,
  enableFreeShipping: true,
  expeditedRate: 24.99,
  expeditedLabel: "Express 2-day",
  standardLabel: "Standard ground (3–5 days)",
};

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.warn("SharedSettings: failed to load from localStorage", e); }
  return { repRates: {}, customerDiscounts: {}, shipping: { ...DEFAULT_SHIPPING } };
}

function saveToStorage(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch (e) { console.warn("SharedSettings: failed to save to localStorage", e); }
}

const stored = loadFromStorage();

const SHARED_SETTINGS = {
  _repRates: stored.repRates || {},
  _customerDiscounts: stored.customerDiscounts || {},
  _shipping: { ...DEFAULT_SHIPPING, ...(stored.shipping || {}) },
  _listeners: [],

  // ── Rep commission ──
  setRepRate(repKey, rate, paySchedule) {
    this._repRates[repKey] = { rate, paySchedule: paySchedule || this._repRates[repKey]?.paySchedule || "15th of month" };
    this._persist();
    this._notify();
  },
  getRepRate(repKey) {
    return this._repRates[repKey] || null;
  },
  getAllRepRates() {
    return { ...this._repRates };
  },

  // ── Customer discount ──
  setCustomerDiscount(accountKey, discount) {
    this._customerDiscounts[accountKey] = discount;
    this._persist();
    this._notify();
  },
  getCustomerDiscount(accountKey) {
    return this._customerDiscounts[accountKey] ?? 0;
  },
  getAllCustomerDiscounts() {
    return { ...this._customerDiscounts };
  },

  // ── Shipping ──
  setShipping(updates) {
    this._shipping = { ...this._shipping, ...updates };
    this._persist();
    this._notify();
  },
  getShipping() {
    return { ...this._shipping };
  },
  /** Calculate shipping cost for a given subtotal */
  calcShipping(subtotal) {
    const s = this._shipping;
    if (s.enableFreeShipping && subtotal >= s.freeThreshold) return 0;
    return s.flatRate;
  },
  /** How much more to qualify for free shipping */
  freeShippingGap(subtotal) {
    const s = this._shipping;
    if (!s.enableFreeShipping) return null;
    if (subtotal >= s.freeThreshold) return 0;
    return s.freeThreshold - subtotal;
  },

  // ── Persistence ──
  _persist() {
    saveToStorage({
      repRates: this._repRates,
      customerDiscounts: this._customerDiscounts,
      shipping: this._shipping,
    });
  },

  // ── Subscribe / notify ──
  subscribe(fn) {
    this._listeners.push(fn);
    return () => { this._listeners = this._listeners.filter(l => l !== fn); };
  },
  _notify() {
    this._listeners.forEach(fn => fn({
      repRates: { ...this._repRates },
      customerDiscounts: { ...this._customerDiscounts },
      shipping: { ...this._shipping },
    }));
  }
};

export default SHARED_SETTINGS;
